import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Table, Button as AntButton, Modal, message, Upload, Form, Select, Input, InputNumber, DatePicker, Tag, Checkbox, Dropdown, Menu } from 'antd';
import { PlusOutlined, DollarOutlined, InboxOutlined, EyeOutlined, MoreOutlined, EditOutlined, DeleteOutlined, UndoOutlined } from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { getAuthHeaders } from '../utils/authHeaders';
import SmartSearch from '../components/SmartSearch';
import dayjs from 'dayjs';
import { Button } from '../modules/inventory/components/button';
import '../styles/VerificationsPage.css';

const { TextArea } = Input;
const { Option } = Select;

interface Invoice {
  _id: string;
  invoiceNumber: string;
  doctor: {
    userId: string;
    name: string;
    email?: string;
  };
  orders: string[];
  invoiceAmount: number;
  payments: Array<{
    amount: number;
    paymentDate: Date;
    paymentMethod?: string;
    notes?: string;
    recordedBy: string;
    recordedAt: Date;
  }>;
  balance: number;
  status: 'active' | 'archived' | 'deleted';
  createdAt: string;
  updatedAt: string;
}

interface Order {
  orderId: string;
  orderStatus: string;
  paymentStatus: string;
  formData?: {
    patientInfo?: {
      doctorName: string;
      firstName: string;
      lastName: string;
    };
  };
  orderItems: Array<{
    item: {
      price: number;
    };
    quantity: number;
  }>;
  createdAt: string;
}

const InvoicingPage: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'active' | 'archived' | 'deleted'>('active');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [invoiceCounts, setInvoiceCounts] = useState({ active: 0, archived: 0, deleted: 0 });

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Loading states for buttons
  const [createLoading, setCreateLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [doctors, setDoctors] = useState<Array<{ userId: string; name: string }>>([]);
  const [selectedDoctor, setSelectedDoctor] = useState<string>('');
  const [availableOrders, setAvailableOrders] = useState<Order[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [invoicePdf, setInvoicePdf] = useState<any>(null);
  
  const [paymentForm] = Form.useForm();
  const [createForm] = Form.useForm();

  const isManufacturer = user?.role === 'manufacturer';

  // Ref to track navigation intent from OrdersPage
  const navigationIntentRef = React.useRef<{invoiceNumber?: string; needsTabCheck?: boolean}>({});

  // Check if navigated from OrdersPage with invoice number
  useEffect(() => {
    const state = location.state as any;
    
    // Handle invoice number navigation
    if (state?.invoiceNumber) {
      console.log('🔍 Navigation with invoice number:', state.invoiceNumber);
      // Store navigation intent
      navigationIntentRef.current = {
        invoiceNumber: state.invoiceNumber,
        needsTabCheck: true
      };
      // Clear the location state
      window.history.replaceState({}, document.title);
      // Set search immediately - tab will be determined after fetch
      setActiveTab('active');  // Start with active tab
      setSearchTerm(state.invoiceNumber);
      setSearchField('invoiceNumber');
    }
    
    // Handle order number navigation from OrdersPage
    if (state?.orderNumber && state?.fromOrdersPage) {
      console.log('🔍 Navigation with order number:', state.orderNumber);
      
      // Function to check which tab the order is in and set the correct tab
      const checkOrderTabAndFilter = async () => {
        try {
          // Fetch all active invoices (no pagination limits for accurate detection)
          const activeResponse = await fetch(`/api/invoices?status=active&limit=10000`, {
            headers: getAuthHeaders(),
            credentials: 'include'
          });
          
          if (activeResponse.ok) {
            const activeData = await activeResponse.json();
            // Check if any invoice contains this order number in their orders array
            const matchingInvoice = activeData.invoices?.find((inv: Invoice) => 
              inv.orders?.includes(state.orderNumber)
            );
            
            if (matchingInvoice) {
              // Found in active tab
              console.log('✅ Order found in active tab, invoice:', matchingInvoice.invoiceNumber);
              setActiveTab('active');
              setSearchTerm(state.orderNumber);
              setSearchField('orderNumber');
              window.history.replaceState({}, document.title);
              return;
            }
          }
          
          // If not found in active, check archived tab
          const archivedResponse = await fetch(`/api/invoices?status=archived&limit=10000`, {
            headers: getAuthHeaders(),
            credentials: 'include'
          });
          
          if (archivedResponse.ok) {
            const archivedData = await archivedResponse.json();
            // Check if any invoice contains this order number in their orders array
            const matchingInvoice = archivedData.invoices?.find((inv: Invoice) => 
              inv.orders?.includes(state.orderNumber)
            );
            
            if (matchingInvoice) {
              // Found in archived tab
              console.log('✅ Order found in archived tab, invoice:', matchingInvoice.invoiceNumber);
              setActiveTab('archived');
              setSearchTerm(state.orderNumber);
              setSearchField('orderNumber');
              window.history.replaceState({}, document.title);
              return;
            }
          }
          
          // If not found in either tab, just search in active tab by default
          console.log('⚠️ Order not found in any tab - searching in active tab');
          setActiveTab('active');
          setSearchTerm(state.orderNumber);
          setSearchField('orderNumber');
          window.history.replaceState({}, document.title);
        } catch (error) {
          console.error('❌ Error checking order tab:', error);
          // Fallback to active tab with search
          setActiveTab('active');
          setSearchTerm(state.orderNumber);
          setSearchField('orderNumber');
          window.history.replaceState({}, document.title);
        }
      };
      
      checkOrderTabAndFilter();
    }
  }, [location]);

  useEffect(() => {
    console.log('📊 Fetching invoices with:', { activeTab, searchTerm, searchField });
    fetchInvoices();
    fetchInvoiceCounts();
    fetchDoctors();
  }, [activeTab, searchTerm, searchField]);

  const fetchInvoiceCounts = async () => {
    try {
      const [activeResponse, archivedResponse /* deletedResponse */] = await Promise.all([
        fetch('/api/invoices?status=active', { headers: getAuthHeaders(), credentials: 'include' }),
        fetch('/api/invoices?status=archived', { headers: getAuthHeaders(), credentials: 'include' }),
        // HIDDEN: Deleted invoices fetch - Keep code for future reuse
        // fetch('/api/invoices?status=deleted', { headers: getAuthHeaders(), credentials: 'include' })
      ]);

      if (activeResponse.ok && archivedResponse.ok /* && deletedResponse.ok */) {
        const activeData = await activeResponse.json();
        const archivedData = await archivedResponse.json();
        // const deletedData = await deletedResponse.json();
        setInvoiceCounts({
          active: activeData.total || 0,
          archived: archivedData.total || 0,
          deleted: 0 // deletedData.total || 0
        });
      }
    } catch (error) {
      console.error('Error fetching invoice counts:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('status', activeTab);
      if (searchTerm) {
        params.append('searchTerm', searchTerm);
        params.append('searchField', searchField);
      }

      const url = `/api/invoices?${params.toString()}`;
      console.log('🌐 Fetching invoices from:', url);
      console.log('🔍 Search params:', { status: activeTab, searchTerm, searchField });

      const response = await fetch(url, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Invoices fetched:', data.invoices?.length || 0, 'invoices');
        setInvoices(data.invoices || []);
        
        // Auto-switch to archived tab if navigating from Orders page and no results found on active tab
        if (navigationIntentRef.current.needsTabCheck && 
            activeTab === 'active' && 
            (!data.invoices || data.invoices.length === 0) &&
            searchField === 'invoiceNumber') {
          console.log('⚠️ No invoices found in active tab - switching to archived tab');
          console.log('🔄 Current search:', { searchTerm, searchField, activeTab });
          navigationIntentRef.current.needsTabCheck = false; // Clear the flag
          setActiveTab('archived'); // This will trigger a re-fetch with archived tab
        } else if (navigationIntentRef.current.needsTabCheck) {
          // Found results or not on active tab anymore, clear the flag
          console.log('✅ Invoice found in current tab - keeping filter active');
          console.log('🔍 Search state:', { searchTerm, searchField, activeTab, resultCount: data.invoices?.length });
          navigationIntentRef.current.needsTabCheck = false;
        }
      } else {
        console.error('❌ Failed to fetch invoices:', response.status);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      message.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await fetch('/api/demographics/doctors', {
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        const doctorsData = result.data || result;
        const uniqueDoctors = Array.from(
          new Map(doctorsData.map((doc: any) => [doc.employeeId, { userId: doc.employeeId, name: doc.name }])).values()
        );
        setDoctors(uniqueDoctors as any);
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  const fetchOrdersForDoctor = async (doctorId: string) => {
    try {
      const response = await fetch(`/api/orders?limit=1000`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const selectedDoctor = doctors.find(d => d.userId === doctorId);
        
        if (!selectedDoctor) {
          setAvailableOrders([]);
          return;
        }

        // Handle different response structures
        const ordersList = data.orders || data.data || data || [];
        
        if (!Array.isArray(ordersList)) {
          console.error('Orders response is not an array:', data);
          setAvailableOrders([]);
          message.error('Invalid orders data received');
          return;
        }

        // Filter for doctor AND payment_received status (not already invoiced)
        const doctorOrders = ordersList.filter((order: Order) => {
          // CRITICAL: Only show orders with payment_received status AND not already invoiced
          if (order.orderStatus !== 'PaymentReceived') return false;
          if (order.paymentStatus === 'invoiced') return false;
          
          const doctorName = order.formData?.patientInfo?.doctorName;
          const selectedDoctorData = (order.formData as any)?.selectedDoctor;
          
          // Match by doctor name
          if (doctorName === selectedDoctor.name) return true;
          
          // Match by selectedDoctor object (employeeId or name)
          if (selectedDoctorData) {
            if (selectedDoctorData.employeeId === selectedDoctor.userId) return true;
            if (selectedDoctorData.name === selectedDoctor.name) return true;
          }
          
          return false;
        });
        
        console.log('📦 Available orders for doctor:', doctorOrders.map(o => ({ orderId: o.orderId, total: calculateOrderTotal(o) })));
        setAvailableOrders(doctorOrders);
        
        if (doctorOrders.length === 0) {
          message.info('No orders with payment received status found for this doctor');
        }
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      message.error('Failed to fetch orders');
    }
  };

  const fetchInvoiceOrders = async (orderIds: string[]) => {
    try {
      // Fetch each order by ID and add to available orders if not already present
      for (const orderId of orderIds) {
        const response = await fetch(`/api/orders/${orderId}`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        });
        
        if (response.ok) {
          const result = await response.json();
          const order = result.data || result;
          
          // Check if this order is already in availableOrders
          setAvailableOrders(prev => {
            const exists = prev.some(o => o.orderId === order.orderId);
            if (!exists) {
              console.log('➕ Adding invoice order to available orders:', order.orderId);
              return [...prev, order];
            }
            return prev;
          });
        }
      }
    } catch (error) {
      console.error('Error fetching invoice orders:', error);
    }
  };

  const handleDoctorChange = (value: string) => {
    setSelectedDoctor(value);
    setSelectedOrders([]);
    fetchOrdersForDoctor(value);
  };

  const handleCreateInvoice = async () => {
    try {
      if (!selectedDoctor || selectedOrders.length === 0) {
        message.error('Please select a doctor and at least one order');
        return;
      }

      if (!invoicePdf) {
        message.error('Please upload an invoice PDF');
        return;
      }

      setCreateLoading(true);
      const reader = new FileReader();
      reader.readAsDataURL(invoicePdf);
      
      reader.onload = async () => {
        try {
          const base64 = reader.result as string;

          const response = await fetch('/api/invoices', {
            method: 'POST',
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
              doctorId: selectedDoctor,
              orders: selectedOrders,
              invoicePdf: base64,
              invoicePdfContentType: invoicePdf.type,
              invoicePdfFilename: invoicePdf.name
            })
          });

          if (response.ok) {
            message.success('Invoice created successfully');
            setCreateModalVisible(false);
            resetCreateForm();
            fetchInvoices();
            fetchInvoiceCounts();
          } else {
            const error = await response.json();
            message.error(error.error || 'Failed to create invoice');
          }
        } catch (error) {
          console.error('Error uploading invoice:', error);
          message.error('Failed to upload invoice');
        } finally {
          setCreateLoading(false);
        }
      };
      
      reader.onerror = () => {
        console.error('Error reading file:', reader.error);
        message.error('Failed to read PDF file');
        setCreateLoading(false);
      };
    } catch (error) {
      console.error('Error creating invoice:', error);
      message.error('Failed to create invoice');
      setCreateLoading(false);
    }
  };

  const handleRecordPayment = async (values: any) => {
    try {
      if (!selectedInvoice) return;

      setPaymentLoading(true);
      const response = await fetch(`/api/invoices/${selectedInvoice._id}/payment`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          amount: values.amount,
          paymentDate: values.paymentDate?.toDate(),
          paymentMethod: values.paymentMethod,
          notes: values.notes
        })
      });

      if (response.ok) {
        await response.json();
        
        if (values.amount > selectedInvoice.balance) {
          Modal.info({
            title: 'Payment Exceeds Balance',
            content: `Payment amount ($${values.amount}) exceeds the balance ($${selectedInvoice.balance}). The payment has been recorded.`
          });
        } else {
          message.success('Payment recorded successfully');
        }

        setPaymentModalVisible(false);
        paymentForm.resetFields();
        setSelectedInvoice(null);
        fetchInvoices();
      } else {
        const error = await response.json();
        message.error(error.error || 'Failed to record payment');
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      message.error('Failed to record payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleArchiveInvoice = async (invoice: Invoice) => {
    if (invoice.balance > 0) {
      Modal.confirm({
        title: 'Archive Invoice with Balance',
        content: `This invoice has an outstanding balance of $${invoice.balance.toFixed(2)}. Are you sure you want to archive it?`,
        onOk: async () => {
          await archiveInvoice(invoice._id);
        }
      });
    } else {
      await archiveInvoice(invoice._id);
    }
  };

  const archiveInvoice = async (invoiceId: string) => {
    try {
      setArchiveLoading(invoiceId);
      const response = await fetch(`/api/invoices/${invoiceId}/archive`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        message.success('Invoice archived successfully');
        fetchInvoices();
        fetchInvoiceCounts();
      } else {
        message.error('Failed to archive invoice');
      }
    } catch (error) {
      console.error('Error archiving invoice:', error);
      message.error('Failed to archive invoice');
    } finally {
      setArchiveLoading(null);
    }
  };

  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setViewModalVisible(true);
  };

  const handleEditInvoice = async (invoice: Invoice) => {
    console.log('📝 Edit Invoice - Full invoice data:', invoice);
    console.log('📝 Invoice orders to pre-select:', invoice.orders);
    
    setSelectedInvoice(invoice);
    setIsEditMode(true);
    
    // Set the selected doctor
    setSelectedDoctor(invoice.doctor.userId);
    
    // Set form field values for edit mode
    createForm.setFieldsValue({
      doctor: invoice.doctor.userId
    });
    
    // Clear PDF for optional new upload
    setInvoicePdf(null);
    
    // Fetch ALL orders for this doctor (with payment_received status)
    await fetchOrdersForDoctor(invoice.doctor.userId);
    
    // Fetch the invoice's current orders to ensure they're available for selection
    // This is important because the order's status might have changed after invoice creation
    await fetchInvoiceOrders(invoice.orders);
    
    // IMPORTANT: Pre-select the invoice's orders AFTER fetching all orders
    console.log('✅ Setting selected orders:', invoice.orders);
    setSelectedOrders(invoice.orders);
    
    // Open the create modal in edit mode
    setCreateModalVisible(true);
  };

  const handleUpdateInvoice = async () => {
    try {
      if (selectedOrders.length === 0) {
        message.error('Please select at least one order');
        return;
      }

      setCreateLoading(true);

      // Recalculate invoice amount based on selected orders
      const calculatedAmount = selectedOrders.reduce((sum, orderId) => {
        const order = availableOrders.find(o => o.orderId === orderId);
        return sum + (order ? calculateOrderTotal(order) : 0);
      }, 0);

      const updateData: any = {
        invoiceNumber: selectedInvoice?.invoiceNumber,
        invoiceAmount: calculatedAmount, // Use recalculated amount based on selected orders
        orders: selectedOrders, // Include the selected orders
      };

      // Include PDF if a new one was uploaded - convert to base64
      if (invoicePdf) {
        const reader = new FileReader();
        reader.readAsDataURL(invoicePdf);
        
        reader.onload = async () => {
          try {
            const base64 = reader.result as string;
            updateData.invoicePdf = base64;
            updateData.invoicePdfContentType = invoicePdf.type;
            updateData.invoicePdfFilename = invoicePdf.name;

            const response = await fetch(`/api/invoices/${selectedInvoice?._id}`, {
              method: 'PUT',
              headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json',
              },
              credentials: 'include',
              body: JSON.stringify(updateData),
            });

            if (response.ok) {
              message.success('Invoice updated successfully');
              setCreateModalVisible(false);
              resetCreateForm();
              fetchInvoices();
              fetchInvoiceCounts();
            } else {
              const error = await response.json();
              message.error(error.message || 'Failed to update invoice');
            }
          } catch (error) {
            console.error('Error updating invoice:', error);
            message.error('Failed to update invoice');
          } finally {
            setCreateLoading(false);
          }
        };

        reader.onerror = () => {
          message.error('Failed to read PDF file');
          setCreateLoading(false);
        };
      } else {
        // No new PDF, just update without PDF
        const response = await fetch(`/api/invoices/${selectedInvoice?._id}`, {
          method: 'PUT',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(updateData),
        });

        if (response.ok) {
          message.success('Invoice updated successfully');
          setCreateModalVisible(false);
          resetCreateForm();
          fetchInvoices();
          fetchInvoiceCounts();
        } else {
          const error = await response.json();
          message.error(error.message || 'Failed to update invoice');
        }
        setCreateLoading(false);
      }
    } catch (error) {
      console.error('Error updating invoice:', error);
      message.error('Failed to update invoice');
      setCreateLoading(false);
    }
  };

  const handleDeleteInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setDeleteModalVisible(true);
  };

  const handleRestoreInvoice = async (invoiceId: string) => {
    try {
      const response = await fetch(`/api/invoices/${invoiceId}/restore`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        message.success('Invoice restored successfully');
        fetchInvoices();
        fetchInvoiceCounts();
      } else {
        const error = await response.json();
        message.error(error.message || 'Failed to restore invoice');
      }
    } catch (error) {
      console.error('Error restoring invoice:', error);
      message.error('Failed to restore invoice');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleteLoading(true);
      const response = await fetch(`/api/invoices/${selectedInvoice?._id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        message.success('Invoice deleted successfully');
        setDeleteModalVisible(false);
        setSelectedInvoice(null);
        fetchInvoices();
        fetchInvoiceCounts();
      } else {
        const error = await response.json();
        message.error(error.message || 'Failed to delete invoice');
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      message.error('Failed to delete invoice');
    } finally {
      setDeleteLoading(false);
    }
  };

  const resetCreateForm = () => {
    setSelectedDoctor('');
    setSelectedOrders([]);
    setAvailableOrders([]);
    setInvoicePdf(null);
    setSelectedInvoice(null);
    setIsEditMode(false);
    createForm.resetFields();
  };

  const handleSearch = (term: string, field: string) => {
    setSearchTerm(term);
    setSearchField(field);
  };

  const calculateOrderTotal = (order: Order) => {
    return order.orderItems.reduce((sum, item) => sum + (item.item.price * item.quantity), 0);
  };

  const columns = [
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      width: 150,
    },
    {
      title: 'Invoice for Doctor',
      dataIndex: ['doctor', 'name'],
      key: 'doctorName',
      width: 200,
    },
    {
      title: 'Invoice Amount',
      dataIndex: 'invoiceAmount',
      key: 'invoiceAmount',
      width: 150,
      render: (amount: number) => `$${amount.toFixed(2)}`,
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      width: 150,
      render: (balance: number) => (
        <Tag color={balance === 0 ? 'green' : 'orange'}>
          ${balance.toFixed(2)}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: any, record: Invoice) => {
        const menuItems: any[] = [];

        // View Invoice - Available for all users
        menuItems.push(
          <Menu.Item 
            key="view"
            icon={<EyeOutlined />}
            onClick={() => handleViewInvoice(record)}
          >
            View Invoice
          </Menu.Item>
        );

        // Manufacturer-specific actions
        if (isManufacturer) {
          // Restore Invoice - Only for deleted invoices
          if (record.status === 'deleted') {
            menuItems.push(
              <Menu.Item 
                key="restore"
                icon={<UndoOutlined />}
                onClick={() => handleRestoreInvoice(record._id)}
              >
                Restore Invoice
              </Menu.Item>
            );
          }

          // Record Payment - Show for active invoices with balance OR archived with balance (not for deleted)
          if (record.balance > 0 && record.status !== 'deleted') {
            menuItems.push(
              <Menu.Item 
                key="payment"
                icon={<DollarOutlined />}
                onClick={() => {
                  setSelectedInvoice(record);
                  setPaymentModalVisible(true);
                }}
              >
                Record Payment
              </Menu.Item>
            );
          }

          // Edit Invoice - Only for active invoices
          if (record.status === 'active') {
            menuItems.push(
              <Menu.Item 
                key="edit"
                icon={<EditOutlined />}
                onClick={() => handleEditInvoice(record)}
              >
                Edit Invoice
              </Menu.Item>
            );
          }

          // Archive - Only for active invoices
          if (record.status === 'active') {
            menuItems.push(
              <Menu.Item 
                key="archive"
                icon={<InboxOutlined />}
                onClick={() => handleArchiveInvoice(record)}
                disabled={archiveLoading === record._id}
              >
                {archiveLoading === record._id ? 'Archiving...' : 'Archive'}
              </Menu.Item>
            );
          }

          // Delete Invoice - Only for active and archived invoices (not for deleted)
          if (record.status !== 'deleted') {
            menuItems.push(
              <Menu.Item 
                key="delete"
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteInvoice(record)}
                danger
              >
                Delete Invoice
              </Menu.Item>
            );
          }
        }

        return (
          <Dropdown
            overlay={<Menu>{menuItems}</Menu>}
            trigger={['click']}
            placement="bottomRight"
          >
            <AntButton
              type="default"
              size="small"
              icon={<MoreOutlined />}
              style={{ 
                padding: '4px 8px',
                minWidth: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Actions"
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="verifications-page">
      <div className="verifications-container">
        <div className="page-header">
          <h1>Invoicing</h1>
          <p>Manage invoices and payments</p>
        </div>

        <SmartSearch
          onSearch={handleSearch}
          placeholder="Search invoices..."
          style={{ marginBottom: '1.5rem' }}
          value={searchTerm}
          field={searchField}
          onChange={(term, field) => {
            setSearchTerm(term);
            setSearchField(field);
          }}
          searchFields={[
            { value: 'all', label: 'All Fields' },
            { value: 'invoiceNumber', label: 'Invoice Number' },
            { value: 'doctorName', label: 'Doctor Name' }
          ]}
        />

        <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {isManufacturer && (
            <div className="product-management-page" style={{ display: 'inline-block', minHeight: 'auto' }}>
              <Button
                variant="default"
                size="sm"
                onClick={() => setCreateModalVisible(true)}
              >
                <PlusOutlined style={{ marginRight: '8px' }} />
                Create Invoice
              </Button>
            </div>
          )}
          {/* HIDDEN: Include Archived Invoices checkbox - Keep code for future reuse */}
          {/* <Checkbox
            checked={includeArchived}
            onChange={(e) => {
              setIncludeArchived(e.target.checked);
              if (e.target.checked && activeTab === 'active') {
                setActiveTab('archived');
              }
            }}
          >
            Include Archived Invoices
          </Checkbox> */}
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <div className="product-management-page" style={{ display: 'inline-block', minHeight: 'auto' }}>
            <div className="flex space-x-1">
              <Button
                variant={activeTab === 'active' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('active')}
              >
                Active ({invoiceCounts.active})
              </Button>
              <Button
                variant={activeTab === 'archived' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('archived')}
              >
                Archive ({invoiceCounts.archived})
              </Button>
              {/* HIDDEN: Deleted tab - Keep code for future reuse */}
              {/* <Button
                variant={activeTab === 'deleted' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('deleted')}
              >
                Deleted ({invoiceCounts.deleted})
              </Button> */}
            </div>
          </div>
        </div>

        <div className="table-container">
          <Table
            columns={columns}
            dataSource={includeArchived ? invoices : invoices.filter(i => i.status === activeTab)}
            rowKey="_id"
            loading={loading}
            pagination={{ pageSize: 20 }}
          />
        </div>

        <Modal
          title={isEditMode ? "Edit Invoice" : "Create Invoice"}
          open={createModalVisible}
          onOk={isEditMode ? handleUpdateInvoice : handleCreateInvoice}
          onCancel={() => {
            setCreateModalVisible(false);
            resetCreateForm();
          }}
          width={800}
          okText={isEditMode ? "Update Invoice" : "Create Invoice"}
          okButtonProps={{
            disabled: isEditMode ? (selectedOrders.length === 0) : (!selectedDoctor || selectedOrders.length === 0 || !invoicePdf),
            loading: createLoading
          }}
        >
          <Form form={createForm} layout="vertical">
            {isEditMode && (
              <Form.Item label="Invoice Number">
                <Input value={selectedInvoice?.invoiceNumber} disabled />
              </Form.Item>
            )}
            
            <Form.Item
              label="Select Doctor"
              name="doctor"
              rules={[{ required: !isEditMode, message: 'Please select a doctor' }]}
            >
              <Select
                placeholder="Select a doctor"
                onChange={handleDoctorChange}
                value={selectedDoctor}
                disabled={isEditMode}
              >
                {doctors.map(doc => (
                  <Option key={doc.userId} value={doc.userId}>{doc.name}</Option>
                ))}
              </Select>
            </Form.Item>

            {selectedDoctor && availableOrders.length > 0 && (
              <>
                <Form.Item label="Select Orders (Payment Received Status)">
                  <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: '4px', padding: '8px' }}>
                    {availableOrders.map(order => (
                      <div key={order.orderId} style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                        <Checkbox
                          checked={selectedOrders.includes(order.orderId)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedOrders([...selectedOrders, order.orderId]);
                            } else {
                              setSelectedOrders(selectedOrders.filter(id => id !== order.orderId));
                            }
                          }}
                        >
                          <strong>Order #{order.orderId}</strong> - {dayjs(order.createdAt).format('MMM DD, YYYY')} - ${calculateOrderTotal(order).toFixed(2)}
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                </Form.Item>

                <Form.Item label="Total Amount">
                  <Input
                    value={`$${selectedOrders.reduce((sum, orderId) => {
                      const order = availableOrders.find(o => o.orderId === orderId);
                      return sum + (order ? calculateOrderTotal(order) : 0);
                    }, 0).toFixed(2)}`}
                    disabled
                  />
                </Form.Item>
              </>
            )}

            <Form.Item
              label={isEditMode ? "Invoice PDF (Optional - Upload New)" : "Upload Invoice PDF"}
              name="pdf"
              rules={[{ required: !isEditMode, message: 'Please upload invoice PDF' }]}
            >
              <Upload.Dragger
                name="file"
                accept=".pdf"
                maxCount={1}
                beforeUpload={(file) => {
                  setInvoicePdf(file);
                  return false;
                }}
                onRemove={() => setInvoicePdf(null)}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {isEditMode 
                    ? "Click or drag new PDF to this area to replace existing PDF" 
                    : "Click or drag PDF to this area to upload"}
                </p>
              </Upload.Dragger>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Record Payment"
          open={paymentModalVisible}
          onOk={() => paymentForm.submit()}
          onCancel={() => {
            setPaymentModalVisible(false);
            paymentForm.resetFields();
            setSelectedInvoice(null);
          }}
          okButtonProps={{
            loading: paymentLoading
          }}
        >
          <Form form={paymentForm} layout="vertical" onFinish={handleRecordPayment}>
            <Form.Item label="Invoice Number">
              <Input value={selectedInvoice?.invoiceNumber} disabled />
            </Form.Item>
            <Form.Item label="Current Balance">
              <Input value={`$${selectedInvoice?.balance.toFixed(2)}`} disabled />
            </Form.Item>
            <Form.Item
              label="Payment Amount"
              name="amount"
              rules={[{ required: true, message: 'Please enter payment amount' }]}
            >
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: '100%' }}
                prefix="$"
              />
            </Form.Item>
            <Form.Item label="Payment Date" name="paymentDate" initialValue={dayjs()}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Payment Method" name="paymentMethod">
              <Select placeholder="Select payment method">
                <Option value="check">Check</Option>
                <Option value="wire_transfer">Wire Transfer</Option>
                <Option value="credit_card">Credit Card</Option>
                <Option value="cash">Cash</Option>
                <Option value="other">Other</Option>
              </Select>
            </Form.Item>
            <Form.Item label="Notes" name="notes">
              <TextArea rows={3} placeholder="Additional notes about this payment" />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Invoice Details"
          open={viewModalVisible}
          onCancel={() => {
            setViewModalVisible(false);
            setSelectedInvoice(null);
          }}
          footer={[
            <AntButton key="close" onClick={() => {
              setViewModalVisible(false);
              setSelectedInvoice(null);
            }}>
              Close
            </AntButton>,
            <AntButton
              key="viewpdf"
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => {
                if (selectedInvoice) {
                  const viewerUrl = `/view-pdf/invoice/${selectedInvoice._id}`;
                  window.open(viewerUrl, '_blank');
                }
              }}
            >
              View PDF
            </AntButton>
          ]}
          width={700}
        >
          {selectedInvoice && (
            <div>
              <p><strong>Invoice Number:</strong> {selectedInvoice.invoiceNumber}</p>
              <p><strong>Doctor:</strong> {selectedInvoice.doctor.name}</p>
              <p><strong>Order Numbers:</strong> {selectedInvoice.orders.join(', ')}</p>
              <p><strong>Invoice Amount:</strong> ${selectedInvoice.invoiceAmount.toFixed(2)}</p>
              <p><strong>Balance:</strong> ${selectedInvoice.balance.toFixed(2)}</p>
              <p><strong>Status:</strong> <Tag color={selectedInvoice.status === 'active' ? 'green' : 'default'}>{selectedInvoice.status}</Tag></p>
              <p><strong>Created:</strong> {dayjs(selectedInvoice.createdAt).format('MMM DD, YYYY h:mm A')}</p>
              
              <h4 style={{ marginTop: '20px' }}>Payment History</h4>
              {selectedInvoice.payments.length > 0 ? (
                <Table
                  size="small"
                  dataSource={selectedInvoice.payments}
                  columns={[
                    {
                      title: 'Date',
                      dataIndex: 'paymentDate',
                      render: (date) => dayjs(date).format('MMM DD, YYYY')
                    },
                    {
                      title: 'Amount',
                      dataIndex: 'amount',
                      render: (amt) => `$${amt.toFixed(2)}`
                    },
                    {
                      title: 'Method',
                      dataIndex: 'paymentMethod',
                      render: (method) => method || 'N/A'
                    },
                    {
                      title: 'Recorded By',
                      dataIndex: 'recordedBy'
                    }
                  ]}
                  pagination={false}
                />
              ) : (
                <p>No payments recorded</p>
              )}
            </div>
          )}
        </Modal>

        {/* Delete Invoice Modal */}
        <Modal
          title="Delete Invoice"
          open={deleteModalVisible}
          onCancel={() => {
            setDeleteModalVisible(false);
            setSelectedInvoice(null);
          }}
          footer={[
            <AntButton key="cancel" onClick={() => {
              setDeleteModalVisible(false);
              setSelectedInvoice(null);
            }}>
              Cancel
            </AntButton>,
            <AntButton
              key="delete"
              type="primary"
              danger
              loading={deleteLoading}
              onClick={handleConfirmDelete}
            >
              Delete
            </AntButton>
          ]}
        >
          <p>Are you sure you want to delete invoice <strong>{selectedInvoice?.invoiceNumber}</strong>?</p>
          <p>The associated orders will be reverted to "not processed" status so they can be used in future invoices.</p>
          <p style={{ color: '#ff4d4f' }}>This action cannot be undone.</p>
        </Modal>
      </div>
    </div>
  );
};

export default InvoicingPage;
